// Tenses & Moods — a complete reference for each Spanish tense with uses,
// the hablar pattern, example sentences, and a tip. Ported from the legacy
// views.tenses; the `sections` data and markup are unchanged, only the
// `(app)` mount-element signature and a smooth scroll-to-top were added.
import { speakButtonHTML } from "../lib/audio.js";

const SECTIONS = [
  {
    id: "presente",
    mood: "Indicative",
    title: "Presente (Present)",
    icon: "☀️",
    color: "bg-sage-50 border-sage-200",
    uses: "Current actions, habitual actions, general truths, and near-future plans.",
    examples: [
      { es: "Hablo español todos los días.", en: "I speak Spanish every day." },
      { es: "El sol sale por el este.", en: "The sun rises in the east." },
      { es: "Mañana voy al gimnasio.", en: "Tomorrow I'm going to the gym." },
      { es: "¿Qué haces?", en: "What are you doing?" },
    ],
    pattern: "hablo · hablas · habla · hablamos · habláis · hablan",
    tip: "Spanish present covers both English \"I speak\" and \"I am speaking\". The progressive (estoy hablando) is only for actions happening right now in the moment.",
  },
  {
    id: "preterito",
    mood: "Indicative",
    title: "Pretérito Indefinido (Simple Past)",
    icon: "📅",
    color: "bg-sand-50 border-sand-200",
    uses: "Completed actions at a specific moment in the past — the storyline events.",
    examples: [
      { es: "Ayer comí pizza.", en: "Yesterday I ate pizza." },
      { es: "Llegamos a las ocho.", en: "We arrived at eight." },
      { es: "El año pasado viajé a México.", en: "Last year I traveled to Mexico." },
      { es: "Ella nació en 1990.", en: "She was born in 1990." },
    ],
    pattern: "hablé · hablaste · habló · hablamos · hablasteis · hablaron",
    tip: "Think of it as snapshots: each event has a defined start and end. Common signals: ayer, anoche, la semana pasada, en 2010.",
  },
  {
    id: "imperfecto",
    mood: "Indicative",
    title: "Pretérito Imperfecto (Imperfect)",
    icon: "🎞️",
    color: "bg-sage-50 border-sage-200",
    uses: "Ongoing, repeated, or background actions in the past — the scenery, not the events.",
    examples: [
      { es: "Cuando era niño, jugaba al fútbol.", en: "When I was a child, I used to play soccer." },
      { es: "Llovía mientras caminábamos.", en: "It was raining while we were walking." },
      { es: "Mi abuela cocinaba todos los domingos.", en: "My grandmother used to cook every Sunday." },
      { es: "Eran las tres de la tarde.", en: "It was three in the afternoon." },
    ],
    pattern: "hablaba · hablabas · hablaba · hablábamos · hablabais · hablaban",
    tip: "Used for \"used to\", \"was -ing\", descriptions, age, time, weather, and emotions in the past. Only three verbs are irregular: ser, ir, ver.",
  },
  {
    id: "preterito-vs-imperfecto",
    mood: "Contrast",
    title: "Pretérito vs Imperfecto",
    icon: "⚖️",
    color: "bg-amber-50 border-amber-200",
    uses: "The hardest contrast for English speakers. Both translate as English past tense, but they tell different stories.",
    examples: [
      { es: "Comía cuando llamaste.", en: "I was eating when you called. (background + event)" },
      { es: "Comí cuando llamaste.", en: "I ate when you called. (two events in sequence)" },
      { es: "Conocí a María en 2010.", en: "I met María in 2010. (one moment)" },
      { es: "Conocía a María.", en: "I knew/used to know María. (ongoing state)" },
    ],
    pattern: "Pretérito = WHAT happened · Imperfecto = WHAT WAS HAPPENING / HOW it was",
    tip: "If you can rewrite it in English as \"was -ing\" or \"used to\", it's almost always imperfecto. If it's a single completed event, it's pretérito.",
  },
  {
    id: "futuro",
    mood: "Indicative",
    title: "Futuro Simple (Future)",
    icon: "🚀",
    color: "bg-sage-50 border-sage-200",
    uses: "Predictions, promises, and probability/conjecture in the present.",
    examples: [
      { es: "Mañana hablaré con él.", en: "Tomorrow I will speak with him." },
      { es: "Nunca olvidaremos este día.", en: "We will never forget this day." },
      { es: "¿Dónde estará María?", en: "I wonder where María is. (probability)" },
      { es: "Serán las diez.", en: "It must be about ten o'clock." },
    ],
    pattern: "hablaré · hablarás · hablará · hablaremos · hablaréis · hablarán",
    tip: "In daily speech, Spanish often uses \"ir a + infinitive\" instead of the future tense: \"voy a hablar\" (I'm going to speak). The future tense is more common in writing and for emphasis.",
  },
  {
    id: "condicional",
    mood: "Indicative",
    title: "Condicional (Conditional)",
    icon: "🤔",
    color: "bg-sage-50 border-sage-200",
    uses: "Polite requests, hypothetical situations, the \"would\" of English.",
    examples: [
      { es: "Me gustaría un café.", en: "I would like a coffee." },
      { es: "¿Podrías ayudarme?", en: "Could you help me?" },
      { es: "Yo en tu lugar, no iría.", en: "If I were you, I wouldn't go." },
      { es: "Dijo que llegaría tarde.", en: "He said he would arrive late." },
    ],
    pattern: "hablaría · hablarías · hablaría · hablaríamos · hablaríais · hablarían",
    tip: "Uses the same irregular stems as the future (tener → tendr-), but with imperfect endings (-ía, -ías, -ía, -íamos, -íais, -ían).",
  },
  {
    id: "presente-subjuntivo",
    mood: "Subjunctive",
    title: "Presente de Subjuntivo (Present Subjunctive)",
    icon: "💭",
    color: "bg-violet-50 border-violet-200",
    uses: "Doubt, desire, emotion, denial, hypotheticals — anything that isn't a stated fact.",
    examples: [
      { es: "Quiero que vengas.", en: "I want you to come." },
      { es: "Es importante que estudies.", en: "It's important that you study." },
      { es: "No creo que sea verdad.", en: "I don't think it's true." },
      { es: "Espero que tengas un buen día.", en: "I hope you have a good day." },
      { es: "Cuando llegues, llámame.", en: "When you arrive, call me. (future event, uncertain)" },
    ],
    pattern: "hable · hables · hable · hablemos · habléis · hablen",
    tip: "Triggered by WEIRDO verbs/phrases: <b>W</b>ishes, <b>E</b>motion, <b>I</b>mpersonal expressions, <b>R</b>ecommendations, <b>D</b>oubt/Denial, <b>O</b>jalá. Built from the yo present indicative with \"opposite\" endings (-ar gets -e endings, -er/-ir get -a endings).",
  },
  {
    id: "imperfecto-subjuntivo",
    mood: "Subjunctive",
    title: "Imperfecto de Subjuntivo (Imperfect Subjunctive)",
    icon: "🌙",
    color: "bg-violet-50 border-violet-200",
    uses: "Past-tense subjunctive: hypothetical, counterfactual, polite expressions, and reported speech triggered by past-tense main clauses.",
    examples: [
      { es: "Si tuviera dinero, viajaría.", en: "If I had money, I would travel." },
      { es: "Quería que vinieras.", en: "I wanted you to come." },
      { es: "Era importante que llegaras a tiempo.", en: "It was important that you arrived on time." },
      { es: "Quisiera un café, por favor.", en: "I would like a coffee, please. (very polite)" },
    ],
    pattern: "hablara / hablase · hablaras / hablases · hablara / hablase · habláramos / hablásemos · hablarais / hablaseis · hablaran / hablasen",
    tip: "Two interchangeable forms (-ra and -se). The -ra form dominates in Latin America and in speech; -se sounds more formal/literary. Both are built from the ellos preterite minus -ron.",
  },
  {
    id: "imperativo",
    mood: "Imperative",
    title: "Imperativo (Commands)",
    icon: "❗",
    color: "bg-rose-50 border-rose-200",
    uses: "Direct commands and instructions.",
    examples: [
      { es: "¡Habla más despacio!", en: "Speak more slowly!" },
      { es: "No hables tan rápido.", en: "Don't speak so fast." },
      { es: "Comamos ahora.", en: "Let's eat now." },
      { es: "Siéntese, por favor.", en: "Please sit down. (formal)" },
    ],
    pattern: "Affirmative tú = 3rd-sing present (habla) · Negative tú = subjunctive (no hables) · All others use the subjunctive",
    tip: "Affirmative tú commands are the only exception — they look like \"él habla\". Everything else (negative tú, usted, nosotros, vosotros negative, ustedes) uses the present subjunctive.",
  },
  {
    id: "perfecto",
    mood: "Compound",
    title: "Pretérito Perfecto (Present Perfect)",
    icon: "✅",
    color: "bg-sand-50 border-sand-200",
    uses: "Actions completed in a time period that's still ongoing (today, this week, this year) — or experiences in life.",
    examples: [
      { es: "He hablado con él hoy.", en: "I have spoken with him today." },
      { es: "¿Has comido sushi alguna vez?", en: "Have you ever eaten sushi?" },
      { es: "Este año hemos viajado mucho.", en: "This year we have traveled a lot." },
    ],
    pattern: "haber (present) + past participle: he/has/ha/hemos/habéis/han + hablado",
    tip: "In Spain, the present perfect is used much more than in Latin America. \"He comido hoy\" (Spain) vs \"Comí hoy\" (Latin America).",
  },
  {
    id: "pluscuamperfecto",
    mood: "Compound",
    title: "Pluscuamperfecto (Past Perfect)",
    icon: "⏪",
    color: "bg-sand-50 border-sand-200",
    uses: "An action that happened BEFORE another past action — \"had done\" in English.",
    examples: [
      { es: "Cuando llegué, ya habían comido.", en: "When I arrived, they had already eaten." },
      { es: "Nunca había visto algo así.", en: "I had never seen anything like that." },
      { es: "Me dijo que había terminado.", en: "He told me he had finished." },
    ],
    pattern: "haber (imperfect) + past participle: había/habías/había/habíamos/habíais/habían + hablado",
    tip: "Always combined with another past tense to show one event came before the other. The English shortcut: if you'd use \"had\" + past participle, use pluscuamperfecto.",
  },
];

export function tensesView(app) {
  const tocItems = SECTIONS.map(
    (s) => `
    <a href="#${s.id}" class="block text-sm text-sage-600 hover:text-sage-800 py-1">
      ${s.icon} ${s.title}
    </a>`
  ).join("");

  const sectionHTML = SECTIONS.map(
    (s) => `
    <article id="${s.id}" class="${s.color} border rounded-2xl p-5 scroll-mt-32">
      <div class="flex items-start justify-between gap-3 flex-wrap mb-2">
        <h3 class="font-bold text-lg text-sage-800">${s.icon} ${s.title}</h3>
        <span class="text-xs px-2 py-1 rounded-full bg-white/70 text-sage-600 font-medium">${s.mood}</span>
      </div>
      <p class="text-sm text-sage-700 mb-3"><b>When to use:</b> ${s.uses}</p>
      <div class="bg-white/60 rounded-lg p-3 mb-3">
        <p class="text-xs uppercase tracking-wide text-sage-500 mb-1">Pattern (hablar)</p>
        <p class="font-mono text-xs text-sage-700 leading-relaxed">${s.pattern}</p>
      </div>
      <div class="space-y-2 mb-3">
        <p class="text-xs uppercase tracking-wide text-sage-500">Examples</p>
        ${s.examples
          .map(
            (ex) => `
          <div class="bg-white/60 rounded-lg p-2.5 text-sm">
            <p class="text-sage-800 flex items-start gap-1.5"><b>${ex.es}</b>${speakButtonHTML(ex.es)}</p>
            <p class="text-sage-500 text-xs italic mt-0.5">${ex.en}</p>
          </div>`
          )
          .join("")}
      </div>
      <p class="text-xs text-sage-600 leading-relaxed bg-white/40 rounded-lg p-2.5">💡 ${s.tip}</p>
    </article>
  `
  ).join("");

  app.innerHTML = `
    <section>
      <div class="mb-6">
        <h2 class="text-2xl font-bold text-sage-800 mb-1">Tenses & Moods</h2>
        <p class="text-sage-600 text-sm">A complete guide to Spanish verb tenses — when to use each, with example sentences.</p>
      </div>

      <div class="grid lg:grid-cols-[220px_1fr] gap-6">
        <aside class="bg-white rounded-2xl border border-sage-100 p-4 self-start lg:sticky lg:top-4">
          <p class="text-xs uppercase tracking-wide text-sage-500 font-medium mb-2">Jump to</p>
          <nav>${tocItems}</nav>
        </aside>

        <div class="space-y-4">
          <article class="bg-gradient-to-br from-sage-50 to-sand-50 border border-sage-100 rounded-2xl p-5">
            <h3 class="font-bold text-sage-800 mb-2">🗺️ The big picture</h3>
            <p class="text-sm text-sage-700 mb-2">Spanish verbs encode <b>three moods</b> and many tenses:</p>
            <ul class="text-sm text-sage-700 space-y-1.5 list-disc pl-5">
              <li><b>Indicative</b> — states facts and reality: <i>presente, pretérito, imperfecto, futuro, condicional</i>.</li>
              <li><b>Subjunctive</b> — expresses doubt, desire, emotion, the unreal: <i>presente de subjuntivo, imperfecto de subjuntivo</i>.</li>
              <li><b>Imperative</b> — gives commands: <i>habla, no hables</i>.</li>
            </ul>
            <p class="text-sm text-sage-700 mt-2">Compound tenses pair <i>haber</i> with a past participle: <i>he hablado</i>, <i>había hablado</i>, <i>habría hablado</i>.</p>
          </article>

          ${sectionHTML}

          <article class="bg-white rounded-2xl border border-sage-100 p-5">
            <h3 class="font-bold text-sage-800 mb-3">🧭 Choosing the right tense — quick decision tree</h3>
            <div class="text-sm text-sage-700 space-y-2">
              <p><b>Is the action happening now or generally true?</b> → Presente</p>
              <p><b>Did it happen at a specific moment in the past?</b> → Pretérito</p>
              <p><b>Was it ongoing, repeated, or descriptive in the past?</b> → Imperfecto</p>
              <p><b>Will it happen?</b> → Futuro (or <i>ir a +</i> infinitive in casual speech)</p>
              <p><b>Would you do it?</b> → Condicional</p>
              <p><b>Does the sentence express a wish, doubt, emotion, or hypothetical?</b> → Subjuntivo</p>
              <p><b>Are you giving a command?</b> → Imperativo</p>
              <p><b>"Have done" / "had done"?</b> → Compound tenses with <i>haber</i></p>
            </div>
          </article>
        </div>
      </div>
    </section>
  `;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
